import React, { useCallback, useMemo } from "react";

import { Grid } from "@mui/material";
import { format } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { FiTrash2 } from "react-icons/fi";

import { useAppState } from "../../../../context/StateContext";
import { GeneralComments } from "../../../../models/general.comments";
import { getUserFullname } from "../../../../util/names";
import {
  CommentBody,
  CommentContainer,
  CommentContainerItem,
  CommentCreatedAt,
  CommentHeaderUserInfosWrapper,
  CommentHeaderUserWrapper,
  CommentMessage,
  CommentType,
  CommentUserAvatarIcon,
  CommentUserIconImage,
  CommentUserName,
  CommmentHeaderContainer,
  CreatedAtText,
  CreatedOnText,
  DeleteModalIconContainer,
} from "./styles";

interface Props {
  comment: GeneralComments;
  isViewMode?: boolean;
  handleDeleteComment?: () => void;
}

const USER_LOGO = require("../../../../assets/profile.png");

export const Comment = ({ comment, isViewMode, handleDeleteComment }: Props) => {
  const { t: translate } = useTranslation();
  const { currentCustomerUser, currentUser } = useAppState();

  const isAllowToDelete = useMemo(() => {
    if (isViewMode) return false;

    if (comment?.user?.id) {
      return currentUser?.id === comment?.user?.id;
    }

    if (comment?.customer?.id) {
      return currentCustomerUser?.customerId === comment?.customer?.id;
    }

    return true;
  }, [currentCustomerUser, comment, isViewMode]);

  const formatCreatedAtDate = useCallback(
    (mobileStyle?: boolean) => {
      if (comment?.createdAt) {
        const currentDate = new Date(comment?.createdAt);

        if (mobileStyle) {
          return `${format(currentDate, "dd")} ${format(currentDate, "LLL")} - ${format(
            currentDate,
            "p"
          )}`;
        }

        return `${format(currentDate, "dd/MM/yyyy")} ${translate("at")} ${format(
          currentDate,
          "p"
        )}`;
      }

      return "";
    },
    [comment?.createdAt]
  );

  const getUserAvatar = useMemo(() => {
    if (currentUser?.avatar) return currentUser?.avatar;

    return USER_LOGO;
  }, [currentUser, currentCustomerUser]);

  return (
    <CommentContainerItem item>
      <CommentContainer item container direction="row" xs={12}>
        <CommmentHeaderContainer item container xs={12}>
          <CommentHeaderUserWrapper>
            <CommentUserAvatarIcon>
              <CommentUserIconImage src={getUserAvatar} alt="user profile avatar" />
            </CommentUserAvatarIcon>
            <CommentHeaderUserInfosWrapper>
              <CommentUserName>
                {getUserFullname(comment?.user || comment?.createdBy || comment?.customer)}
              </CommentUserName>
              {!!comment?.createdAt && (
                <CommentCreatedAt>
                  <CommentType>{comment?.type ? `${comment?.type} • ` : ""}</CommentType>
                  <CreatedOnText>
                    {translate("createdOn")} {""}
                  </CreatedOnText>
                  <CreatedAtText>{formatCreatedAtDate()}</CreatedAtText>
                  <CreatedAtText mobileStyled>{formatCreatedAtDate(true)}</CreatedAtText>
                </CommentCreatedAt>
              )}
            </CommentHeaderUserInfosWrapper>
          </CommentHeaderUserWrapper>

          {isAllowToDelete && (
            <DeleteModalIconContainer onClick={() => handleDeleteComment?.()}>
              <FiTrash2 size={16} color="#D92D20" />
            </DeleteModalIconContainer>
          )}
        </CommmentHeaderContainer>
        <Grid item xs={12} md={12} lg={12}>
          <CommentBody>
            <CommentMessage>{comment?.message}</CommentMessage>
          </CommentBody>
        </Grid>
      </CommentContainer>
    </CommentContainerItem>
  );
};
